import React, { useCallback, useRef, useState } from 'react';

import { styled } from '@mui/material';

import { NavigationLinks } from '@mewa/types';
import { scrollToDropdown } from '@mewa/ui';

import { addServiceSegmentGroups } from '../../../helpers/service-segment.helper';
import { ServiceSegmentLink } from '../../header/types';
import { useQuickNavigationState } from '../state';
import type { IndustryHierarchy } from '../types';
import DropdownMenu from './dropdown';
import QuickNavDropdownButton from './dropdown-button';
import SubmitButton from './submit-button';

export const QuickNavigation = ({
  className = '',
  industries,
  serviceSegments,
  translations,
  nonClothingSlug,
  cleanroomSlug,
  productOverviewSlug,
  onSearch,
  showHeadline = true,
  featureDev = false,
  navigationLinks,
}: {
  className?: string;
  industries: IndustryHierarchy[];
  serviceSegments: ServiceSegmentLink[];
  langPrefix: string;
  translations: {
    quickNavAllIndustries: string;
    quickNavAlProducts: string;
    quickNavIndustryPlaceholder: string;
    quickNavProductsPlaceholder: string;
    quickNavHeadline: string;
    search: string;
    showProducts: string;
  };
  nonClothingSlug: string;
  cleanroomSlug: string;
  productOverviewSlug: string;
  onSearch: (urlWithoutLangPrefix: string) => void;
  showHeadline?: boolean;
  featureDev?: boolean;
  navigationLinks: NavigationLinks;
}) => {
  const industryMenuRef = useRef<HTMLButtonElement>(null);
  const serviceSegmentMenuRef = useRef<HTMLButtonElement>(null);

  const state = useQuickNavigationState(
    industries,
    serviceSegments,
    translations.quickNavAllIndustries,
    translations.quickNavAlProducts,
  );

  const [isIndustryOpen, setIndustryOpen] = useState(false);
  const [isServiceSegmentOpen, setServiceSegmentOpen] = useState(false);

  const scrollToIndustries = useRef(
    scrollToDropdown('industry-menu-button', state.industryOptions, {
      dropdownItemHeight: 32,
    }),
  );

  const scrollToServiceSegments = useRef(
    scrollToDropdown('product-menu-button', state.serviceSegments, {
      dropdownItemHeight: 32,
      dropdownPadding: 140,
    }),
  );

  const toggleIndustryOpen = useCallback(() => {
    if (!isIndustryOpen) {
      scrollToIndustries.current();
    }
    setIndustryOpen(!isIndustryOpen);
  }, [setIndustryOpen, isIndustryOpen]);

  const toggleServiceSegmentOpen = useCallback(() => {
    if (!isServiceSegmentOpen) {
      scrollToServiceSegments.current();
    }
    setServiceSegmentOpen(!isServiceSegmentOpen);
  }, [setServiceSegmentOpen, isServiceSegmentOpen]);

  const onSelectIndustry = (idx: number) => {
    state.selectIndustry(idx);
    setIndustryOpen(false);
    setServiceSegmentOpen(true);
  };

  const onSelectServiceSegment = (idx: number) => {
    state.selectServiceSegment(idx);
    setServiceSegmentOpen(false);
  };

  const industryLabel = state.industryLabel(
    translations.quickNavIndustryPlaceholder,
  );
  const serviceSegmentLabel = state.serviceSegmentLabel(
    translations.quickNavProductsPlaceholder,
  );

  return (
    <>
      {showHeadline && <Title>{translations.quickNavHeadline}</Title>}
      <QuickNavContainer className={className}>
        <QuickNavMenuItem>
          <QuickNavDropdownButton
            id="industry-menu-button"
            ref={industryMenuRef}
            onClick={toggleIndustryOpen}
            hasOpenMenu={isIndustryOpen}
          >
            {industryLabel}
          </QuickNavDropdownButton>

          <DropdownMenu
            items={state.industryOptions}
            open={isIndustryOpen}
            anchor={industryMenuRef.current}
            onClose={() => setIndustryOpen(false)}
            onSelect={onSelectIndustry}
            name="industry-menu"
            labelledby="industry-menu-button"
            activeItem={state.selectedIndustry}
          />
        </QuickNavMenuItem>
        <QuickNavMenuItem>
          <QuickNavDropdownButton
            id="product-menu-button"
            ref={serviceSegmentMenuRef}
            onClick={toggleServiceSegmentOpen}
            hasOpenMenu={isServiceSegmentOpen}
          >
            {serviceSegmentLabel}
          </QuickNavDropdownButton>
          <DropdownMenu
            items={addServiceSegmentGroups(
              state.serviceSegmentOptions,
              navigationLinks.navigation,
            )}
            open={isServiceSegmentOpen}
            anchor={serviceSegmentMenuRef.current}
            onClose={() => setServiceSegmentOpen(false)}
            onSelect={onSelectServiceSegment}
            name="industry-menu"
            labelledby="industry-menu-button"
            activeItem={state.selectedServiceSegment}
          />
        </QuickNavMenuItem>
        <SubmitButton
          aria-label={translations.search}
          label={translations.showProducts}
          onClick={() =>
            onSearch(
              `${state.link(productOverviewSlug, nonClothingSlug, cleanroomSlug)}`,
            )
          }
        />
      </QuickNavContainer>
    </>
  );
};

const QuickNavContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  position: 'relative',
  gridTemplateColumns: 'auto',
  gridTemplateRows: 'auto auto auto',
  gridRowGap: '15px',

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr auto',
    gridTemplateRows: 'auto auto',
    gridRowGap: '10px',
    gridColumnGap: '20px',
  },
}));

const Title = styled('h2')(({ theme }) => ({
  ...theme.typography.h6,
  margin: '0 0 15px',

  [theme.breakpoints.up('sm')]: {
    color: 'var(--color-white)',
    gridColumn: '1 / 3',
    marginBottom: '10px',
  },
}));

const QuickNavMenuItem = styled('div')({
  flexGrow: 1,
  position: 'relative',
});
